import { ApiError } from "@/lib/exceptions/api-error";
import api from "@/lib/axios";

export interface Creator {
  id: number;
  country: string;
  cpm: string | number | null; // Update this line
  name: string;
  avatar: string;
  email: string;
  handle: string;
  user: {
    name: string;
    avatar: string;
    email: string;
    is_onboarded: number;
  };
  accounts: Array<{
    id: number;
    platform: string;
    username: string;
    display_name: string;
    avatar: string;
    followers: number;
    url: string;
  }>;
}

export interface Campaign {
  uuid: string;
  name: string;
  description: string;
  creator_instructions?: string;
  status: string;
  posts: number;
  creator_count: number;
  account_count: number;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  engagement_rate: number;
  createdAt: string;
  platforms: string[];
  starts_at: string;
  ended_at: string | null;
  ends_at: string | null;
  last_synced_at: string | null;
  budget_type: "none" | "cpm" | "per_post";
  manager: {
    name: string;
    avatar: string;
  };
  is_admin: boolean;
  budget: number | null;
  used_budget: number;
  base_cpm: number;
  invitations: Array<{
    id: number;
    uuid: string;
    email: string;
    user: {
      name: string;
      avatar: string;
      email: string;
    } | null;
    invited_by: {
      name: string;
      avatar: string;
    };
  }>;
  sync:
    | {
        total: number;
        completed: number;
      }
    | false;
  invite_code: string;
  creators: Creator[];
  assets_link: string | null;
  timezone: string;
  earnings_per_post: number | null;
  location_cpms: Array<{
    type: "continent" | "country";
    location: string;
    cpm: string;
  }> | null;
}

interface CreateCampaignResponse {
  success: boolean;
  data: {
    campaign: Campaign;
  };
}

interface CampaignInviteResponse {
  success: boolean;
  data: {
    invited_by: {
      name: string;
      avatar: string;
    };
    space: {
      name: string;
      icon: string | null;
      creators: number;
    };
    campaign: {
      name: string;
      description: string;
    };
  };
}

interface CPMResponse {
  success: boolean;
  data: {
    cpm: string;
    country: string;
  };
}

interface GetCampaignsResponse {
  success: boolean;
  data: {
    campaigns: Campaign[];
  };
}

export interface Post {
  uuid: string;
  platform: string;
  platform_id: string;
  title: string;
  caption: string;
  link: string;
  length: number;
  status: string;
  views: number;
  engagement_rate: string;
  likes: number;
  comments: number;
  shares: number;
  bookmarks: number;
  posted_at: string;
  creator: Creator;
  account: {
    name: string;
    handle: string;
    avatar: string;
  };
  is_manual: boolean;
  is_syncing: boolean;
  can_sync: boolean;
  earnings: number | null;
  synced_at: string | null;
  isNew?: boolean;
  thumbnail: string;
  count_towards_budget: boolean;
}

interface PaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
}

interface GetPostsResponse {
  success: boolean;
  data: {
    posts: Post[];
  };
}

interface GetCampaignResponse {
  success: boolean;
  data: {
    campaign: Campaign;
  };
}

interface UpdateCampaignResponse {
  success: boolean;
  data: {
    campaign: Campaign;
  };
}

interface InviteCreatorsResponse {
  success: boolean;
  data: {
    invites: Array<{
      id: number;
      uuid: string;
      email: string;
      user: {
        name: string;
        avatar: string;
        email: string;
      } | null;
      invited_by: {
        name: string;
        avatar: string;
      };
    }>;
  };
}

interface ConnectedAccount {
  id: number;
  username: string;
  display_name: string;
  avatar: string | null;
  platform: "tiktok" | "instagram" | "youtube";
  added_at: string;
  status: "active" | "pending" | "failed";
}

interface GetAccountsResponse {
  success: boolean;
  data: {
    accounts: ConnectedAccount[];
  };
}

export class CampaignService {
  private static instance: CampaignService;
  private baseUrl: string = "/campaigns";

  private constructor() {}

  public static getInstance(): CampaignService {
    if (!CampaignService.instance) {
      CampaignService.instance = new CampaignService();
    }
    return CampaignService.instance;
  }

  async create(data: {
    name: string;
    description: string;
    creatorInstructions?: string;
    assetsLink?: string;
    startType: "now" | "later";
    startDate?: Date;
    budgetType: "none" | "per_post" | "cpm";
    budget?: string;
    earningsPerPost?: string;
    baseCpm?: string;
    locationCpms?: Array<{
      type: "continent" | "country";
      location: string;
      cpm: string;
    }>;
    space: string;
  }): Promise<Campaign> {
    try {
      const response = await api.post<CreateCampaignResponse>(
        `${this.baseUrl}/create`,
        data
      );
      return response.data.data.campaign;
    } catch (error: any) {
      if (error.response) {
        const errorData = error.response.data;

        // Handle 403 Forbidden (e.g., campaign limit reached)
        if (error.response.status === 403) {
          throw new ApiError(
            403,
            errorData.message || "You've reached your campaign limit"
          );
        }

        // Handle validation errors
        if (error.response.status === 422) {
          throw new ApiError(422, errorData.message, errorData.errors);
        }

        throw new ApiError(
          error.response.status,
          errorData.message || "Failed to create campaign"
        );
      }

      throw new ApiError(500, "An unexpected error occurred");
    }
  }

  async inviteCreators(
    campaignId: string,
    emails: string[]
  ): Promise<InviteCreatorsResponse> {
    const response = await api.post<InviteCreatorsResponse>(
      `/campaigns/${campaignId}/invite`,
      { invitations: emails }
    );
    return response.data;
  }

  async getInvite(
    uuid: string,
    email: string,
    location?: string
  ): Promise<CampaignInviteResponse> {
    try {
      const response = await api.get<CampaignInviteResponse>(
        `${this.baseUrl}/invite/get`,
        {
          params: {
            invite: uuid,
            email: email,
            ...(location && { location }),
          },
        }
      );

      if (!response.data.success) {
        throw new Error("Failed to fetch invite");
      }

      return response.data;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new ApiError(
          (error as any).response?.status || 500,
          (error as any).response?.data?.message || error.message
        );
      }
      throw new ApiError(500, "An unexpected error occurred");
    }
  }

  async acceptInvite(uuid: string, email: string): Promise<void> {
    try {
      const response = await api.post(`${this.baseUrl}/invite/accept`, {
        invite: uuid,
        email: email,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to accept invite");
      }
    } catch (error) {
      throw new ApiError(
        (error as any).response?.status || 500,
        (error as any).response?.data?.message ||
          "Failed to accept campaign invite"
      );
    }
  }

  async getInviteCPM(
    uuid: string,
    email: string,
    country: string
  ): Promise<CPMResponse> {
    try {
      const response = await api.get<CPMResponse>(
        `${this.baseUrl}/invite/cpm`,
        {
          params: {
            invite: uuid,
            email: email,
            country: country,
          },
        }
      );

      if (!response.data.success) {
        throw new Error("Failed to fetch CPM rate");
      }

      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiError(
          (error as any).response?.status || 500,
          (error as any).response?.data?.message || error.message
        );
      }
      throw new ApiError(500, "An unexpected error occurred");
    }
  }

  async getCampaigns(space: string): Promise<Campaign[]> {
    try {
      const response = await api.get<GetCampaignsResponse>(
        `${this.baseUrl}/get`,
        {
          params: { space },
        }
      );

      if (!response.data.success) {
        throw new Error("Failed to fetch campaigns");
      }

      return response.data.data.campaigns;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.message || "Failed to fetch campaigns"
      );
    }
  }

  async addPost(
    campaignId: string,
    urls: string[],
    countTowardsBudget: boolean = false
  ): Promise<void> {
    try {
      const response = await api.post(`/campaigns/${campaignId}/add-post`, {
        urls,
        countTowardsBudget,
      });

      if (!response.data.success) {
        throw new Error("Failed to add post");
      }
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.message || "Failed to add post"
      );
    }
  }

  async getPosts(
    campaignId: string,
    params: {
      page?: number;
      search?: string;
      sort_by?: string;
      sort_direction?: "asc" | "desc";
    } = {}
  ): Promise<Post[]> {
    try {
      const response = await api.get<GetPostsResponse>(
        `${this.baseUrl}/${campaignId}/posts`,
        { params }
      );

      if (!response.data.success) {
        throw new Error("Failed to fetch posts");
      }

      return response.data.data.posts;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.message || "Failed to fetch posts"
      );
    }
  }

  async getCampaign(uuid: string): Promise<Campaign> {
    try {
      const response = await api.get<GetCampaignResponse>(
        `${this.baseUrl}/${uuid}`
      );

      if (!response.data.success) {
        throw new Error("Failed to fetch campaign");
      }

      return response.data.data.campaign;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.message || "Failed to fetch campaign"
      );
    }
  }

  async syncPost(postUuid: string): Promise<void> {
    try {
      await api.get(`/posts/${postUuid}/sync`);
    } catch (error) {
      throw new ApiError(
        (error as any).response?.status || 500,
        (error as any).response?.data?.message || "Failed to sync post"
      );
    }
  }

  async removePost(postUuid: string): Promise<void> {
    try {
      await api.get(`/posts/${postUuid}/delete`);
    } catch (error) {
      throw new ApiError(
        (error as any).response?.status || 500,
        (error as any).response?.data?.message || "Failed to remove post"
      );
    }
  }

  async sync(
    campaignId: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.get<{ success: boolean; message?: string }>(
        `${this.baseUrl}/${campaignId}/sync`
      );

      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.message || "Failed to sync campaign"
      );
    }
  }

  async exportPosts(campaignId: string): Promise<Blob> {
    const response = await api.get(`/campaigns/${campaignId}/export`, {
      responseType: "blob",
      headers: {
        Accept: "text/csv",
      },
    });

    // Create a new blob with the CSV MIME type
    return new Blob([response.data], { type: "text/csv" });
  }

  async updateCampaign(
    uuid: string,
    data: Partial<Campaign>
  ): Promise<Campaign> {
    const response = await api.post<UpdateCampaignResponse>(
      `/campaigns/${uuid}/update`,
      data
    );

    if (!response.data.success) {
      throw new Error("Failed to update campaign");
    }

    return response.data.data.campaign;
  }

  async deleteCampaign(uuid: string, name: string): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/${uuid}/delete`, { name });
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.message || "Failed to delete campaign"
      );
    }
  }

  async getStats(uuid: string): Promise<{
    historical: Array<{ views: number; date: string }>;
    today: number;
  }> {
    const response = await api.get(`/campaigns/${uuid}/stats`);
    return response.data.data;
  }

  async setCreatorCpm(
    campaignId: string,
    creatorId: number,
    cpm: number
  ): Promise<void> {
    await api.post(`/campaigns/${campaignId}/cpm`, {
      creator_id: creatorId,
      cpm: cpm,
    });
  }

  async removeInvitation(
    campaignId: string,
    invitationId: number
  ): Promise<{ success: boolean; message?: string }> {
    const response = await api.delete(
      `/campaigns/${campaignId}/invitations/${invitationId}`
    );

    return response.data;
  }

  async getSharedCampaign(uuid: string) {
    try {
      const response = await api.post<{
        success: boolean;
        data: {
          campaign: Campaign;
          posts: Post[];
          stats: {
            historical: Array<{ views: number; date: string }>;
            today: number;
          };
        };
      }>(`/campaigns/${uuid}/sharable`);

      if (!response.data.success) {
        return null;
      }

      return response.data.data;
    } catch (error) {
      console.error("Error fetching shared campaign:", error);
      throw new ApiError(
        (error as any).response?.status || 500,
        (error as any).response?.data?.message ||
          "Failed to fetch shared campaign"
      );
    }
  }

  async generateShareLink(campaignId: number) {
    try {
      const response = await api.post(`/campaigns/${campaignId}/share`);
      return response.data.uuid;
    } catch (error) {
      //throw this.handleError(error);
    }
  }

  async revokeShareLink(campaignId: number) {
    try {
      await api.delete(`/campaigns/${campaignId}/share`);
    } catch (error) {
      //throw this.handleError(error);
    }
  }

  async endCampaign(uuid: string): Promise<void> {
    try {
      const response = await api.get(`${this.baseUrl}/${uuid}/end`);

      if (!response.data.success) {
        throw new Error("Failed to end campaign");
      }
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.message || "Failed to end campaign"
      );
    }
  }

  async startCampaign(uuid: string): Promise<void> {
    try {
      const response = await api.get(`${this.baseUrl}/${uuid}/start`);

      if (!response.data.success) {
        throw new Error("Failed to start campaign");
      }
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.message || "Failed to start campaign"
      );
    }
  }

  async getAccounts(uuid: string): Promise<ConnectedAccount[]> {
    try {
      const response = await api.get<GetAccountsResponse>(
        `${this.baseUrl}/${uuid}/accounts`
      );

      if (!response.data.success) {
        throw new Error("Failed to fetch accounts");
      }

      return response.data.data.accounts;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.message || "Failed to fetch accounts"
      );
    }
  }

  async removeAccount(campaignId: string, accountId: number): Promise<void> {
    try {
      const response = await api.delete(
        `${this.baseUrl}/${campaignId}/accounts/${accountId}`
      );

      if (!response.data.success) {
        throw new Error("Failed to remove account");
      }
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.message || "Failed to remove account"
      );
    }
  }
}
